// src/pages/BlockedPolicies.jsx
import React, { useState, useContext, useEffect } from "react";
import { Card, CardBody, Input } from "reactstrap";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { ColorModeContext } from "../../index";
import { request } from "../../utils/request";
import "./dashboard.scss";

/* Base palette */
const PALETTE = [
  "#0b1d51",
  "#F2914a",
  "#27ae60",
  "#7a8c99",
  "#2d9cdb",
  "#E84855",
  "#ff9f1c",
  "#6f42c1",
  "#00bcd4",
  "#9c27b0",
  "#795548",
  "#9E9E9E",
];

const INVALID_POLICY_VALUES = new Set([
  "",
  "value",
  "null",
  "undefined",
  "n/a",
  "na",
  "none",
  "unknown",
  "-",
  "--",
]);

/** safe array parser for many API response shapes */
function safeArrayFrom(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.data)) return resp.data;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.docs)) return resp.docs;
  if (resp && resp.success && Array.isArray(resp.data)) return resp.data;
  return [];
}

/** normalize policy label and skip placeholders like "value" */
function normalizePolicyName(raw) {
  if (raw === null || raw === undefined) return null;
  const name = String(raw).trim();
  if (!name) return null;

  const lower = name.toLowerCase();
  if (INVALID_POLICY_VALUES.has(lower)) return null;

  return name;
}

/** get the first valid policy name from a list of candidates */
function firstValidPolicy(...candidates) {
  for (const candidate of candidates) {
    const normalized = normalizePolicyName(candidate);
    if (normalized) return normalized;
  }
  return null;
}

/**
 * Extract policy name from different possible API shapes.
 * Fixes the issue where a placeholder like "value" is shown in the graph.
 */
function getPolicyNameFromLog(log) {
  if (!log) return null;

  const ev = log.eventData || {};
  const policyViolation = ev.policyViolation || {};

  return firstValidPolicy(
    policyViolation.policyViolated,
    policyViolation.policyName,
    policyViolation.policy,
    ev.policyViolated,
    ev.policyName,
    ev.policy_name,
    log.policyViolated,
    log.policyName,
    log.policy_name
  );
}

/** detect if event is policy violation */
function isPolicyViolationType(t) {
  if (!t) return false;
  const s = String(t).toLowerCase().replace(/[\s_-]+/g, "");
  return s.includes("policyviolation") || s.includes("policy_violation");
}

/** stable unique color by index */
function colorForIndex(index) {
  if (index < PALETTE.length) return PALETTE[index];

  // generate extra distinct colors beyond palette
  const hue = (index * 137.508) % 360; // golden angle
  return `hsl(${hue}, 70%, 50%)`;
}

/**
 * Assign one unique color per policy name.
 * This guarantees:
 * 1) same policy name always gets same color in the chart
 * 2) different policy names do not reuse the same color unless palette is exhausted
 */
function buildColorMap(policyNames) {
  const map = new Map();
  policyNames.forEach((name, index) => {
    map.set(name, colorForIndex(index));
  });
  return map;
}

/* Reuse injected mini-widget CSS */
const injectMiniWidgetStyles = () => {
  if (document.getElementById("mini-widget-polish")) return;

  const css = `
    .mw-light-root { --bg: #fff; --tile-bg: #f9fafc; --muted: #666; --text: #0b1220; --tile-border: rgba(6,15,34,0.04); --icon-ghost: rgba(3,7,18,0.03); --shadow: 0 1px 2px rgba(0,0,0,0.06); }
    .mw-dark-root { --bg: #252b3b; --tile-bg: #2f3649; --muted: #c9c9c9; --text: #f5f5f5; --tile-border: rgba(255,255,255,0.05); --icon-ghost: rgba(255,255,255,0.03); --shadow: 0 1px 3px rgba(0,0,0,0.35); }

    .mini-tile {
      position: relative;
      overflow: hidden;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: transform 160ms ease, box-shadow 160ms ease;
    }
    .mini-tile::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02));
      mix-blend-mode: overlay;
    }
    .mini-tile:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(11,20,37,0.08); }

    .mini-left { z-index: 1; flex: 1; overflow: hidden; padding-right: 8px; min-width: 0; }
    .mini-title { margin: 0; font-size: 0.88rem; color: var(--text); font-weight: 600; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; }
    .mini-value { margin: 4px 0 0; font-size: 1rem; color: var(--text); font-weight: 600; }
    .mini-desc { display: block; margin-top: 6px; color: var(--muted); font-size: 0.72rem; }

    .mini-right { z-index: 1; display:flex; flex-direction: column; align-items: flex-end; gap: 6px; min-width: 44px; margin-left: 0.4rem; }
    .mini-icon-circle { width: 36px; height: 36px; border-radius: 8px; display:flex; align-items:center; justify-content:center; box-shadow: inset 0 -6px 14px rgba(0,0,0,0.04); background: var(--icon-ghost); color: var(--text); flex-shrink: 0; }

    .mini-live-badge {
      position: absolute;
      right: 8px;
      top: 8px;
      z-index: 2;
      font-size: 10px;
      padding: 4px 8px;
      border-radius: 999px;
      font-weight: 700;
      background: linear-gradient(90deg, rgba(0,191,99,0.12), rgba(0,191,99,0.04));
      color: #036b27;
      box-shadow: 0 2px 6px rgba(3, 107, 39, 0.06);
      pointer-events: none;
    }

    .polish-card {
      border-radius: 12px;
      overflow: visible;
      position: relative;
    }
    .polish-card .polish-body {
      padding: 0.9rem;
      box-sizing: border-box;
    }
    .polish-card .card-sheen::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02));
      mix-blend-mode: overlay;
      border-radius: inherit;
    }

    .polish-header { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:6px; flex-wrap:nowrap; }
    .polish-title { margin:0; font-size:1rem; font-weight:600; color:var(--text); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

    .polish-chart-wrap { min-height:200px; display:flex; align-items:center; justify-content:center; position: relative; }

    .loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 6;
      gap: 8px;
      pointer-events: none;
    }
    .loading-message {
      font-weight: 600;
      font-size: 14px;
      color: var(--text);
    }
    .loading-dots { display:flex; gap:6px; align-items:center; pointer-events: none; }
    .loading-dots .dot { width:8px; height:8px; border-radius:50%; background: var(--text); opacity: 0.25; animation: blink 1s infinite ease-in-out; }
    .loading-dots .dot:nth-child(1){ animation-delay: 0s; }
    .loading-dots .dot:nth-child(2){ animation-delay: 0.15s; }
    .loading-dots .dot:nth-child(3){ animation-delay: 0.3s; }
    @keyframes blink { 0% { opacity: 0.25; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-4px); } 100% { opacity: 0.25; transform: translateY(0); } }

    @media (max-width:720px) {
      .mini-title { white-space: normal; }
      .polish-header { flex-wrap: wrap; align-items: flex-start; }
    }
  `;

  const s = document.createElement("style");
  s.id = "mini-widget-polish";
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
};

/** attempt to fetch a large set of logs, fallback to default logs endpoint */
async function fetchLogsAll() {
  try {
    const r = await request("logs?limit=10000&sort=-createdAt", { method: "GET" });
    const arr = safeArrayFrom(r);
    if (arr.length > 0) return arr;
  } catch (e) {
    console.debug("fetchLogsAll: large-limit fetch failed:", e?.message || e);
  }

  try {
    const r = await request("logs", { method: "GET" });
    return safeArrayFrom(r);
  } catch (e) {
    console.error("fetchLogsAll: fallback logs fetch failed:", e);
    return [];
  }
}

export default function BlockedPolicies() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  const [dept, setDept] = useState("all");
  const [departments, setDepartments] = useState(["all"]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    injectMiniWidgetStyles();
  }, []);

  const fetchAndBuild = async () => {
    setLoading(true);

    try {
      const [usersResp, logsRaw] = await Promise.all([
        request("user", { method: "GET" }),
        fetchLogsAll(),
      ]);

      const users = safeArrayFrom(usersResp);
      const logs = Array.isArray(logsRaw) ? logsRaw : safeArrayFrom(logsRaw);

      const userDept = new Map();
      const deptSet = new Set();

      for (const u of users) {
        const id = u && (u._id || u.id) ? String(u._id || u.id) : null;
        const d = u && (u.department || "Unassigned")
          ? String(u.department || "Unassigned")
          : "Unassigned";

        if (id) userDept.set(id, d);
        if (d) deptSet.add(d);
      }

      setDepartments(["all", ...Array.from(deptSet).sort((a, b) => a.localeCompare(b))]);

      const counts = new Map();

      for (const lg of logs) {
        const et = lg?.eventType || lg?.event_type || lg?.log_type || "";
        if (!isPolicyViolationType(et)) continue;

        const rawPolicy = getPolicyNameFromLog(lg);
        if (!rawPolicy) continue;

        const uid = lg?.userId
          ? String(lg.userId)
          : lg?.user_id
            ? String(lg.user_id)
            : null;

        const logDept = uid && userDept.has(uid) ? userDept.get(uid) : "Unassigned";
        if (dept !== "all" && logDept !== dept) continue;

        counts.set(rawPolicy, (counts.get(rawPolicy) || 0) + 1);
      }

      const entries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);

      const colorMap = buildColorMap(entries.map(([name]) => name));

      const data = entries.map(([name, value]) => ({
        name,
        value,
        color: colorMap.get(name),
      }));

      setChartData(data);
    } catch (err) {
      console.error("BlockedPolicies fetch error:", err);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndBuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dept]);

  const total = chartData.reduce((s, d) => s + (d.value || 0), 0);

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const p = payload[0].payload;
    const pct = total ? Math.round(((p.value || 0) / total) * 100) : 0;

    return (
      <div
        style={{
          backgroundColor: isDark ? "#1f2937" : "#fff",
          border: isDark
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid rgba(0,0,0,0.06)",
          padding: 8,
          borderRadius: 6,
          color: isDark ? "#fff" : "#111827",
        }}
      >
        <div style={{ fontWeight: 600 }}>{p.name}</div>
        <div style={{ fontSize: 13 }}>
          {p.value} occurrence{p.value === 1 ? "" : "s"}
        </div>
        <div style={{ fontSize: 12, color: isDark ? "#c9c9c9" : "#6b7280" }}>
          {pct}%
        </div>
      </div>
    );
  };

  const renderLegend = (props) => {
    const { payload } = props || {};
    if (!payload || !payload.length) return null;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {payload.map((entry) => {
          const itemColor =
            entry.color ||
            entry.payload?.fill ||
            entry.payload?.color ||
            "#ccc";

          return (
            <div
              key={`${entry.value}-${itemColor}`}
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  background: itemColor,
                  display: "inline-block",
                  borderRadius: 3,
                }}
              />
              <div style={{ fontSize: 13, color: isDark ? "#f5f5f5" : "#0b1d51" }}>
                {entry.value}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const rootClass = mode === "dark" ? "mw-dark-root" : "mw-light-root";

  return (
    <div className={`widget-card ${rootClass}`} style={{ width: "100%" }}>
      <Card
        className="polish-card"
        style={{
          backgroundColor: isDark ? "#252b3b" : "#fff",
          border: isDark ? "none" : "1px solid #e6e9ee",
          height: "100%",
          borderRadius: 12,
        }}
      >
        <div
          className="card-sheen"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 12,
            pointerEvents: "none",
          }}
        />

        <CardBody
          className={`polish-body ${isDark ? "text-white" : ""}`}
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            padding: 0,
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            className="polish-header"
            style={{ padding: "0.9rem 0.9rem 0 0.9rem" }}
          >
            <div style={{ minWidth: 0, flex: "1 1 auto", marginRight: 12 }}>
              <h5
                className="polish-title"
                style={{
                  color: isDark ? "#f5f5f5" : "#0b1d51",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Blocked Policies
              </h5>
              <div
                style={{
                  fontSize: 12,
                  color: isDark ? "#bfc7d6" : "#6b7280",
                  marginTop: 4,
                }}
              >
                {total} total blocked event{total === 1 ? "" : "s"}
                {dept !== "all" ? ` in ${dept}` : ""}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: "0 0 auto" }}>
              <div style={{ textAlign: "right", minWidth: 160 }}>
                <Input
                  type="select"
                  value={dept}
                  onChange={(e) => setDept(e.target.value)}
                  style={{
                    width: 150,
                    borderRadius: 6,
                    padding: "6px 8px",
                    marginTop: 0,
                  }}
                >
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d === "all" ? "All Departments" : d}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
          </div>

          <div
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              gap: 12,
              padding: "0 0.9rem 0.6rem 0.9rem",
            }}
          >
            <div
              style={{ flex: "1 1 60%", height: 260, position: "relative" }}
              className="polish-chart-wrap"
            >
              {loading && (
                <div
                  className="loading-overlay"
                  style={{
                    backgroundColor: isDark
                      ? "rgba(15,23,42,0.6)"
                      : "rgba(255,255,255,0.7)",
                    color: isDark ? "#fff" : "#111827",
                  }}
                >
                  <div className="loading-message">Calculating…</div>
                  <div className="loading-dots" aria-hidden="true">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </div>
                </div>
              )}

              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius="90%"
                    paddingAngle={2}
                    startAngle={90}
                    endAngle={-270}
                    isAnimationActive={!loading}
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        stroke={isDark ? "#0b0b0b" : "#fff"}
                        strokeWidth={1}
                      />
                    ))}
                  </Pie>

                  <Tooltip content={<TooltipContent />} />

                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    iconType="square"
                    content={renderLegend}
                    wrapperStyle={{ right: 0 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
} 
